/**
 * How a chance of winning looks on the board: in the classic palette red for poor odds, amber
 * for an even fight, green for good odds. The attack-target rings and the labels above them
 * share it so both agree, and each palette has its own (`palette.ts`).
 */
export interface Rgb {
  r: number;
  g: number;
  b: number;
}

/** The three colours a ramp of odds runs through. */
export interface OddsColors {
  poor: Rgb;
  even: Rgb;
  good: Rgb;
}

/** The classic ramp: red, amber, green. */
export const CLASSIC_ODDS: OddsColors = {
  poor: { r: 1, g: 0.24, b: 0.22 },
  even: { r: 1, g: 0.7, b: 0.18 },
  good: { r: 0.28, g: 0.88, b: 0.42 },
};

const clamp01 = (v: number) => (Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0.5);

/** Colour for a win chance 0 … 1, blending poor → even over the poor half and even → good over the good half. */
export function oddsColor(probability: number, colors: OddsColors = CLASSIC_ODDS): Rgb {
  const p = clamp01(probability);
  const { poor, even, good } = colors;
  const [from, to, t] = p < 0.5 ? [poor, even, p * 2] : [even, good, (p - 0.5) * 2];
  return {
    r: from.r + (to.r - from.r) * t,
    g: from.g + (to.g - from.g) * t,
    b: from.b + (to.b - from.b) * t,
  };
}

/** The chance as the tablets write percentages: "53 %". */
export function oddsLabel(probability: number): string {
  return `${Math.round(clamp01(probability) * 100)} %`;
}

/** CSS colour of a win chance, for canvas drawing. */
export function oddsCss(probability: number, colors: OddsColors = CLASSIC_ODDS): string {
  const { r, g, b } = oddsColor(probability, colors);
  return `rgb(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)})`;
}
